import React, { useEffect, useState } from 'react';
import { getAllCreditSummaries, CreditSummaryResponse } from '../../utils/creditService';
import { useAuth } from '../../providers/AuthProvider';
import styles from '../../styles/common/CreditLineFilter.module.scss';

interface CreditLineOption {
  id: number;
  label: string;
}

interface CreditLineFilterProps {
  selectedCreditLineId?: number;
  onCreditLineChange: (creditLineId: number | undefined) => void;
  placeholder?: string;
  className?: string;
}

function CreditLineFilter({ 
  selectedCreditLineId, 
  onCreditLineChange, 
  placeholder = "All Credit Lines",
  className
}: CreditLineFilterProps) {
  const { isAuthenticated } = useAuth();
  const [creditLineOptions, setCreditLineOptions] = useState<CreditLineOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCreditLines = async () => {
      if (!isAuthenticated) {
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const creditLines = await getAllCreditSummaries();
        
        const options: CreditLineOption[] = creditLines.map((creditLine: CreditSummaryResponse) => ({
          id: creditLine.available_balances.id,
          label: creditLine.label
        }));

        setCreditLineOptions(options);
      } catch (err: any) {
        console.error('Error fetching credit lines:', err);
        setError('Failed to load credit lines');
      } finally {
        setLoading(false);
      }
    };

    fetchCreditLines();
  }, [isAuthenticated]);

  const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    onCreditLineChange(value === '' ? undefined : parseInt(value, 10));
  };

  return (
    <div className={`${styles.creditLineFilter} ${className || ''}`}>
      <label htmlFor="creditLineSelect" className={styles.label}>
        Credit Line
      </label>
      <div className={styles.selectWrapper}>
        <select
          id="creditLineSelect"
          value={selectedCreditLineId || ''}
          onChange={handleSelectChange}
          disabled={loading || !!error}
          className={styles.select}
        >
          <option value="">{placeholder}</option>
          {creditLineOptions.map((option) => (
            <option key={option.id} value={option.id}>
              {option.label}
            </option>
          ))}
        </select>
        
        {loading && (
          <div className={styles.loadingIndicator}>
            <svg className={styles.spinner} viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" strokeDasharray="31.416" strokeDashoffset="31.416">
                <animate attributeName="stroke-dasharray" dur="2s" values="0 31.416;15.708 15.708;0 31.416" repeatCount="indefinite"/>
                <animate attributeName="stroke-dashoffset" dur="2s" values="0;-15.708;-31.416" repeatCount="indefinite"/>
              </circle>
            </svg>
          </div>
        )}
      </div>
      
      {error && (
        <div className={styles.error}>
          {error}
        </div>
      )}
    </div>
  );
}

export default CreditLineFilter;
